import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Upload, 
  FileText, 
  Briefcase, 
  AlertCircle, 
  CheckCircle,
  History,
  ArrowRight,
  Sparkles,
  Crown,
  Loader2,
  Target,
  Zap,
  Shield
} from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { t } = useTranslation();
  const { user, isPremium } = useAuth();
  const navigate = useNavigate();
  
  const [cvText, setCvText] = useState('');
  const [jobText, setJobText] = useState('');
  const [cvFile, setCvFile] = useState(null);
  const [jobFile, setJobFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (!analyzing) { setAnalysisProgress(0); return; }
    setAnalysisProgress(5);
    const id = setInterval(() => setAnalysisProgress(p => p >= 88 ? 88 : p + (88 - p) * 0.1 + 0.8), 800);
    return () => clearInterval(id);
  }, [analyzing]);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  // Load history on mount
  React.useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/analysis/history`, {
        withCredentials: true
      });
      setHistory(data);
    } catch (err) {
      console.error('Error loading history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleFileUpload = async (file, type) => {
    if (!file) return;
    
    const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type)) {
      setError(t('dashboard.error_format'));
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await axios.post(`${API_URL}/api/documents/extract`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (type === 'cv') {
        setCvText(data.text);
        setCvFile(file);
      } else {
        setJobText(data.text);
        setJobFile(file);
      }
    } catch (err) {
      setError(err.response?.data?.detail || t('dashboard.error_extraction'));
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = useCallback((e, type) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    handleFileUpload(file, type);
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleAnalyze = async () => {
    if (!cvText.trim() || !jobText.trim()) {
      setError(t('dashboard.error_fields'));
      return;
    }

    setAnalyzing(true);
    setError('');

    try {
      const { data } = await axios.post(
        `${API_URL}/api/analysis/create`,
        { cv_text: cvText, job_offer_text: jobText },
        { withCredentials: true }
      );

      navigate(`/resultats/${data.id}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && detail.includes('limite gratuite')) {
        setError(t('dashboard.free_limit_error'));
      } else {
        setError(detail || t('dashboard.error_analysis'));
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 50) return 'text-amber-600 bg-amber-100';
    return 'text-red-600 bg-red-100';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return t('dashboard.score_excellent');
    if (score >= 60) return t('dashboard.score_good');
    if (score >= 40) return t('dashboard.score_average');
    return t('dashboard.score_weak');
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <Header />
      
      <div className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Welcome */}
          <div className="mb-8">
            <h1 
              className="text-2xl sm:text-3xl font-semibold text-zinc-900 mb-2"
              style={{ fontFamily: "'Outfit', sans-serif" }}
              data-testid="dashboard-title"
            >
              {t('dashboard.greeting', { name: user?.name })}
            </h1>
            <p className="text-zinc-600 text-lg">
              {isPremium ? (
                <span className="flex items-center gap-2">
                  <Crown className="h-5 w-5 text-amber-500" />
                  <span>{t('dashboard.premium_badge')}</span>
                </span>
              ) : (
                t('dashboard.subtitle')
              )}
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3" data-testid="dashboard-error">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-700 font-medium">{error}</p>
                {error.includes('limite') && (
                  <Button 
                    variant="link" 
                    className="p-0 h-auto text-red-700 underline"
                    onClick={() => navigate('/tarifs')}
                  >
                    {t('dashboard.see_options')}
                  </Button>
                )}
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Upload Section */}
            <div className="lg:col-span-2 space-y-6">
              {/* Step indicator */}
              <div className="flex items-center gap-3 text-sm text-zinc-500 mb-2">
                <span className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-bold">1</span>
                  {t('dashboard.step_cv')}
                </span>
                <div className="flex-1 h-px bg-zinc-300" />
                <span className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-bold">2</span>
                  {t('dashboard.step_offer')}
                </span>
                <div className="flex-1 h-px bg-zinc-300" />
                <span className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-zinc-300 text-zinc-600 text-xs flex items-center justify-center font-bold">3</span>
                  {t('dashboard.step_analysis')}
                </span>
              </div>

              {/* CV Upload */}
              <div className="bg-white border border-zinc-200 rounded-2xl p-6 sm:p-8" data-testid="cv-section">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                    <FileText className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-zinc-900 text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {t('dashboard.cv_title')}
                    </h2>
                    <p className="text-sm text-zinc-500">{t('dashboard.cv_subtitle')}</p>
                  </div>
                </div>

                <Tabs defaultValue="upload" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-4 bg-zinc-100 p-1 rounded-lg">
                    <TabsTrigger value="upload" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
                      {t('dashboard.import_file')}
                    </TabsTrigger>
                    <TabsTrigger value="text" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
                      {t('dashboard.paste_text')}
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="upload">
                    <div
                      className={`border-2 border-dashed rounded-xl p-10 text-center transition-all cursor-pointer ${cvFile ? 'border-green-400 bg-green-50' : 'border-zinc-300 hover:border-blue-400 hover:bg-blue-50'}`}
                      onDrop={(e) => handleDrop(e, 'cv')}
                      onDragOver={handleDragOver}
                      onClick={() => document.getElementById('cv-input').click()}
                      data-testid="cv-dropzone"
                    >
                      <input
                        id="cv-input"
                        type="file"
                        accept=".pdf,.docx"
                        className="hidden"
                        onChange={(e) => handleFileUpload(e.target.files[0], 'cv')}
                      />
                      {cvFile ? (
                        <>
                          <CheckCircle className="h-10 w-10 text-green-600 mx-auto mb-3" />
                          <p className="text-green-700 font-medium">{cvFile.name}</p>
                          <p className="text-sm text-green-600 mt-1">{t('dashboard.click_replace')}</p>
                        </>
                      ) : (
                        <>
                          <Upload className="h-10 w-10 text-zinc-400 mx-auto mb-3" />
                          <p className="text-zinc-700 font-medium mb-1">{t('dashboard.cv_drag')}</p>
                          <p className="text-sm text-zinc-400">{t('dashboard.cv_drag_sub')}</p>
                        </>
                      )}
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="text">
                    <Textarea
                      placeholder="Colle le contenu de ton CV ici...&#10;&#10;Exemple:&#10;Jean Dupont - Développeur Web&#10;&#10;Expérience:&#10;- 3 ans chez TechCorp..."
                      value={cvText}
                      onChange={(e) => setCvText(e.target.value)}
                      className="min-h-[200px] border-zinc-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl"
                      data-testid="cv-textarea"
                    />
                  </TabsContent>
                </Tabs>

                {cvText && !cvFile && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-green-700 font-medium">{t('dashboard.cv_ready', { count: cvText.length })}</span>
                  </div>
                )}
              </div>

              {/* Job Offer Upload */}
              <div className="bg-white border border-zinc-200 rounded-2xl p-6 sm:p-8" data-testid="job-section">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                    <Briefcase className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-zinc-900 text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {t('dashboard.offer_title')}
                    </h2>
                    <p className="text-sm text-zinc-500">{t('dashboard.offer_subtitle')}</p>
                  </div>
                </div>

                <Tabs defaultValue="text" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-4 bg-zinc-100 p-1 rounded-lg">
                    <TabsTrigger value="text" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
                      {t('dashboard.paste_text')}
                    </TabsTrigger>
                    <TabsTrigger value="upload" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">
                      {t('dashboard.import_file')}
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="text">
                    <Textarea
                      placeholder="Colle l'offre d'emploi ici...&#10;&#10;Copie simplement le texte de l'annonce depuis LinkedIn, Indeed, ou le site de l'entreprise."
                      value={jobText}
                      onChange={(e) => setJobText(e.target.value)}
                      className="min-h-[200px] border-zinc-200 focus:border-amber-500 focus:ring-amber-500 rounded-xl"
                      data-testid="job-textarea"
                    />
                  </TabsContent>

                  <TabsContent value="upload">
                    <div
                      className={`border-2 border-dashed rounded-xl p-10 text-center transition-all cursor-pointer ${jobFile ? 'border-green-400 bg-green-50' : 'border-zinc-300 hover:border-amber-400 hover:bg-amber-50'}`}
                      onDrop={(e) => handleDrop(e, 'job')}
                      onDragOver={handleDragOver}
                      onClick={() => document.getElementById('job-input').click()}
                      data-testid="job-dropzone"
                    >
                      <input
                        id="job-input"
                        type="file"
                        accept=".pdf,.docx"
                        className="hidden"
                        onChange={(e) => handleFileUpload(e.target.files[0], 'job')}
                      />
                      {jobFile ? (
                        <>
                          <CheckCircle className="h-10 w-10 text-green-600 mx-auto mb-3" />
                          <p className="text-green-700 font-medium">{jobFile.name}</p>
                          <p className="text-sm text-green-600 mt-1">{t('dashboard.click_replace')}</p>
                        </>
                      ) : (
                        <>
                          <Upload className="h-10 w-10 text-zinc-400 mx-auto mb-3" />
                          <p className="text-zinc-700 font-medium mb-1">{t('dashboard.offer_drag')}</p>
                          <p className="text-sm text-zinc-400">{t('dashboard.offer_drag_sub')}</p>
                        </>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>

                {jobText && !jobFile && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-green-700 font-medium">{t('dashboard.offer_ready', { count: jobText.length })}</span>
                  </div>
                )}
              </div>

              {/* Analyze Button */}
              <Button
                onClick={handleAnalyze}
                disabled={analyzing}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl py-6 text-lg disabled:opacity-50 shadow-lg shadow-blue-600/20"
                data-testid="analyze-button"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="h-6 w-6 mr-3 animate-spin" />
                    {t('dashboard.analyzing')}
                  </>
                ) : uploading ? (
                  <>
                    <Loader2 className="h-6 w-6 mr-3 animate-spin" />
                    {t('dashboard.extracting')}
                  </>
                ) : (
                  <>
                    <Zap className="h-6 w-6 mr-3" />
                    {t('dashboard.analyze_btn')}
                  </>
                )}
              </Button>

              {analyzing && (
                <div className="bg-white border border-zinc-200 rounded-xl p-4">
                  <div className="flex justify-between text-sm text-zinc-600 mb-2">
                    <span>
                      {analysisProgress < 25 ? t('dashboard.progress_1') :
                       analysisProgress < 50 ? t('dashboard.progress_2') :
                       analysisProgress < 75 ? t('dashboard.progress_3') :
                       t('dashboard.progress_4')}
                    </span>
                    <span className="font-medium text-blue-600">{Math.round(analysisProgress)}%</span>
                  </div>
                  <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-600 rounded-full transition-all duration-500" style={{ width: `${analysisProgress}%` }} />
                  </div>
                </div>
              )}

              {/* Trust Badge */}
              <div className="flex items-center justify-center gap-2 text-sm text-zinc-500">
                <Shield className="h-4 w-4 text-green-600" />
                <span>{t('dashboard.trust_badge')}</span>
              </div>
            </div>

            {/* History Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white border border-zinc-200 rounded-2xl p-6 sticky top-24" data-testid="history-section">
                <div className="flex items-center gap-2 mb-5">
                  <History className="h-5 w-5 text-zinc-600" />
                  <h2 className="font-semibold text-zinc-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {t('dashboard.history_title')}
                  </h2>
                </div>

                {loadingHistory ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-zinc-400 mb-3" />
                    <p className="text-zinc-400 text-sm">{t('dashboard.history_loading')}</p>
                  </div>
                ) : history.length === 0 ? (
                  <div className="text-center py-12">
                    <Target className="h-12 w-12 text-zinc-300 mx-auto mb-4" />
                    <p className="text-zinc-500 font-medium mb-2">{t('dashboard.history_empty')}</p>
                    <p className="text-zinc-400 text-sm">{t('dashboard.history_empty_sub')}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {history.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => navigate(`/resultats/${item.id}`)}
                        className="w-full p-4 border border-zinc-200 rounded-xl hover:border-blue-300 hover:bg-blue-50/50 transition-all text-left group"
                        data-testid={`history-item-${item.id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-2xl font-bold px-2 py-0.5 rounded-lg ${getScoreColor(item.score_global)}`}>
                            {item.score_global}%
                          </span>
                          <ArrowRight className="h-5 w-5 text-zinc-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                        </div>
                        <p className="text-sm font-medium text-zinc-700">{getScoreLabel(item.score_global)}</p>
                        <p className="text-xs text-zinc-400 mt-1">
                          {new Date(item.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                        </p>
                      </button>
                    ))}
                  </div>
                )}

                {/* Upgrade prompt for free users */}
                {!isPremium && history.length >= 1 && (
                  <div className="mt-6 p-4 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Crown className="h-5 w-5 text-amber-500" />
                      <p className="text-sm font-semibold text-amber-800">{t('dashboard.upgrade_title')}</p>
                    </div>
                    <p className="text-xs text-amber-700 mb-3">
                      {t('dashboard.upgrade_body')}
                    </p>
                    <Button
                      className="w-full bg-amber-500 hover:bg-amber-600 text-white font-medium"
                      onClick={() => navigate('/tarifs')}
                      data-testid="upgrade-prompt"
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      {t('dashboard.see_options')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Dashboard;
